<template>
  <div id="app">
    <Toast />
    <ConfirmDialog />
    
    <div v-if="authInitialized">
      <div v-if="isLoggedIn" class="app-container">
        <TopBar @toggle-sidebar="toggleSidebar" />
        <div class="main-content">
          <Sidebar :class="{ 'sidebar-hidden': isMobile && !sidebarVisible }" />
          <div class="content-area" :class="{ 'content-full': isMobile && !sidebarVisible }">
            <div v-if="isMobile && sidebarVisible" class="sidebar-overlay" @click="closeSidebar"></div>
            <router-view />
          </div>
        </div>
      </div>
      
      <div v-else>
        <router-view />
      </div>
    </div>
    
    <div v-else class="loading-container">
      <div class="loading-spinner">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        <p>Yükleniyor...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import TopBar from '@/components/Layout/TopBar.vue';
import Sidebar from '@/components/Layout/Sidebar.vue';

export default {
  name: 'App',
  components: {
    TopBar,
    Sidebar
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    const isLoggedIn = computed(() => store.getters.isAuthenticated);
    const authInitialized = computed(() => store.getters.authInitialized);
    const isMobile = computed(() => store.getters.isMobile);
    const sidebarVisible = computed(() => store.getters.sidebarVisible);

    const handleResize = () => {
      store.dispatch('checkMobile');
    };

    const toggleSidebar = () => {
      store.dispatch('toggleSidebar');
    };

    const closeSidebar = () => {
      if (isMobile.value) {
        store.commit('SET_SIDEBAR_VISIBLE', false);
      }
    };

    onMounted(async () => {
      await store.dispatch('initializeAuth');
      store.dispatch('checkMobile');
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    return {
      isLoggedIn,
      authInitialized,
      isMobile,
      sidebarVisible,
      toggleSidebar,
      closeSidebar
    };
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: #fef2f2;
  min-height: 100vh;
  color: #374151;
  line-height: 1.6;
}

.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.content-area {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  background-color: #fef2f2;
  transition: all 0.3s ease;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.loading-spinner {
  text-align: center;
  color: white;
}

.loading-spinner i {
  margin-bottom: 1rem;
}

.loading-spinner p {
  margin: 0;
  font-size: 1.1rem;
}

/* Mobil stilleri */
.sidebar-hidden {
  transform: translateX(-100%);
}

.content-full {
  width: 100%;
  padding: 1rem;
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

/* KIRMIZI-BEYAZ TEMA GÜNCELLEMELERİ */

/* PrimeVue Component Overrides - Kırmızı Tema */
.p-card {
  box-shadow: 0 1px 3px 0 rgba(220, 38, 38, 0.1), 0 1px 2px 0 rgba(220, 38, 38, 0.06);
  border: 1px solid #fecaca;
  border-radius: 12px;
  background: white;
}

.p-card .p-card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.75rem;
}

.p-card .p-card-content {
  padding: 1.25rem;
}

.p-datatable {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(220, 38, 38, 0.1);
  border: 1px solid #fecaca;
}

.p-datatable .p-datatable-thead > tr > th {
  background-color: #fef2f2;
  color: #dc2626;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 1rem 1rem;
  border-color: #fecaca;
}

.p-datatable .p-datatable-tbody > tr > td {
  padding: 1rem 1rem;
  border-color: #fecaca;
  font-size: 0.875rem;
}

.p-datatable .p-datatable-tbody > tr {
  transition: background-color 0.2s;
}

.p-datatable .p-datatable-tbody > tr:hover {
  background-color: #fef2f2;
}

/* Dialog Improvements */
.p-dialog {
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(220, 38, 38, 0.25);
  border: 1px solid #fecaca;
}

.p-dialog .p-dialog-header {
  background: white;
  border-bottom: 1px solid #fecaca;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 1.5rem;
}

.p-dialog .p-dialog-content {
  background: white;
  padding: 1.5rem;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

/* Button Improvements - Kırmızı Tema */
.p-button {
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s;
  padding: 0.75rem 1.5rem;
}

.p-button:not(.p-button-text):not(.p-button-outlined):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.15);
}

.p-button-primary {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border: 1px solid #dc2626;
}

.p-button-primary:hover {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  border-color: #b91c1c;
}

/* Input Improvements */
.p-inputtext {
  border-radius: 8px;
  border: 1px solid #d1d5db;
  transition: all 0.2s;
  padding: 0.75rem 1rem;
}

.p-inputtext:enabled:hover {
  border-color: #fca5a5;
}

.p-inputtext:enabled:focus {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  border-color: #dc2626;
}

/* TabView Improvements */
.p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
  background: #dc2626;
  color: white;
}

/* Status Badges - Kırmızı Tema Uyumlu */
.status-active {
  background-color: #dcfce7;
  color: #166534;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #bbf7d0;
}

.status-completed {
  background-color: #dbeafe;
  color: #1e40af;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #bfdbfe;
}

.status-cancelled {
  background-color: #fee2e2;
  color: #dc2626;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #fecaca;
}

.warning-badge {
  background-color: #fef3c7;
  color: #92400e;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #fcd34d;
}

/* Role Badges */
.role-admin {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
}

.role-driver {
  background-color: #dcfce7;
  color: #166534;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #bbf7d0;
}

.role-healthworker {
  background-color: #fef3c7;
  color: #92400e;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #fcd34d;
}

/* Custom Utility Classes */
.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  color: #64748b;
  font-size: 1rem;
}

.stat-card {
  text-align: center;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #fecaca;
  box-shadow: 0 1px 3px rgba(220, 38, 38, 0.1);
}

.stat-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #dc2626;
  opacity: 0.8;
}

.stat-value {
  font-size: 2.25rem;
  font-weight: 700;
  color: #dc2626;
  display: block;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Grid Improvements */
.grid {
  margin: 0 -0.75rem;
}

.col-12, .col-6, .col-8, .col-4, .col-3 {
  padding: 0.75rem;
}

/* Responsive */
@media (max-width: 768px) {
  .content-area {
    padding: 1rem;
  }
  
  .p-dialog {
    width: 95% !important;
    margin: 1rem;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
}

/* Animation Improvements */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Custom Scrollbar */
.content-area::-webkit-scrollbar {
  width: 6px;
}

.content-area::-webkit-scrollbar-track {
  background: #fef2f2;
}

.content-area::-webkit-scrollbar-thumb {
  background: #fecaca;
  border-radius: 3px;
}

.content-area::-webkit-scrollbar-thumb:hover {
  background: #fca5a5;
}

/* Özel Kırmızı Tema Sınıfları */
.red-gradient-bg {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.red-light-bg {
  background-color: #fef2f2;
}

.red-border {
  border-color: #fecaca;
}

.red-text {
  color: #dc2626;
}

.red-shadow {
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
}
</style>